/**
 * linearSnapshot — lift a Linear team read into a SemanticSnapshot.
 *
 * The same thin adapter shape as notionSnapshot, over the SAME shared builder:
 * Linear issues are reference objects, IssueRelations are typed cross-
 * references (the relation type is the cue), the sub-issue tree is the parent
 * chain, and a label/state color is the category color. That this is nearly
 * identical to notionSnapshot is the point — one reference-dialect builder.
 */

import type { LinearTeamRead } from './linearSemanticApi';
import type { MiroColorRule, RelationshipCueRule, TypeMapRule } from '../fieldMappingRegistry';
import { LINEAR_DEFAULT_RELATIONSHIP_MAP, LINEAR_UNNAMED_RELATION } from './profiles/linear';
import {
  buildReferenceSnapshot,
  type ReferenceDialectRead,
} from './referenceDialectSnapshot';
import type { SemanticSnapshot } from './snapshot';

export interface LinearSnapshotMaps {
  relationshipMap?: RelationshipCueRule[];
  typeMap?: TypeMapRule[];
  colorRules?: MiroColorRule[];
}

export function buildLinearSnapshot(
  read: LinearTeamRead,
  maps: LinearSnapshotMaps,
  options: { capturedAt?: string } = {},
): SemanticSnapshot {
  const dialectRead: ReferenceDialectRead = {
    connectorType: 'linear',
    container: { id: read.team.id, title: read.team.name, url: read.team.url },
    objects: read.issues.map(issue => ({
      externalId: issue.externalId,
      ...(issue.externalUrl ? { externalUrl: issue.externalUrl } : {}),
      title: issue.title,
      ...(issue.description ? { description: issue.description } : {}),
      ...(issue.categoryColor ? { categoryColor: issue.categoryColor } : {}),
      ...(issue.parentExternalId ? { parentExternalId: issue.parentExternalId } : {}),
      ...(issue.attributes ? { attributes: issue.attributes } : {}),
      relations: issue.relations.map(r => ({ cue: r.type, targetExternalIds: r.targetExternalIds })),
    })),
  };

  return buildReferenceSnapshot(
    dialectRead,
    maps,
    {
      relationshipMap: LINEAR_DEFAULT_RELATIONSHIP_MAP,
      containmentCueValue: 'parent',
      unnamedRelation: LINEAR_UNNAMED_RELATION,
    },
    options,
  );
}
